import React, { useEffect, useMemo, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCompanySelection } from "@/contexts/ActiveCompanyContext";
import { toast } from "sonner";
import { CalendarDays, Filter, Palmtree, Clock, Sparkles, Users2, Printer } from "lucide-react";
import { MONTHS_IT, WEEKDAYS_SHORT } from "@/lib/constants";
import { SuiteCard, SuiteButton, SuiteEmployeePicker, SuiteEyebrow, SuiteBadge, SuiteMonthPicker } from "@/components/suite";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { downloadBlob } from "@/lib/download";
import { formatDateLongIT } from "@/foundation/formatting";
import { fmtHours } from "@/foundation/formatting";

/**
 * LeaveCalendar — vista mensile stile Google Calendar delle ferie/permessi
 * di TUTTI i dipendenti dell'azienda. Uso principale: admin/client controlla
 * sovrapposizioni per evitare che troppe persone siano assenti nello stesso periodo.
 *
 * Convenzioni di colore (bordo sinistro della "pill"):
 *  - approved  → suite-accent (verde)
 *  - pending   → suite-warning (arancio)
 *  - rejected  → suite-error (rosso, semitrasparente)
 *  - cancelled → suite-neutral-400 (grigio, dashed)
 */

const KIND_META = {
  FERIE:            { label: "Ferie",         icon: Palmtree, dot: "bg-suite-warning" },
  PERMESSI:         { label: "Permessi",      icon: Clock,    dot: "bg-suite-secondary" },
  PERMESSI_104:     { label: "Permessi L.104", icon: Clock,    dot: "bg-suite-secondary" },
  EX_FESTIVITA:     { label: "Ex festività",  icon: Sparkles, dot: "bg-suite-accent" },
  PERMESSI_STUDIO:  { label: "P. Studio",     icon: Clock,    dot: "bg-suite-primary" },
  MALATTIA:         { label: "Malattia",      icon: Clock,    dot: "bg-suite-error" },
  CONG_PARENTALE:   { label: "Congedo parentale", icon: Users2, dot: "bg-suite-secondary" },
  MATERNITA:        { label: "Maternità",      icon: Users2,   dot: "bg-suite-secondary" },
  ALLATTAMENTO:     { label: "Allattamento",  icon: Clock,    dot: "bg-suite-secondary" },
  RECUPERO:         { label: "Recupero",      icon: Clock,    dot: "bg-suite-primary" },
  ASSENZA_NR:       { label: "Assenza non retribuita", icon: Clock, dot: "bg-suite-neutral-500" },
};

const STATUS_META = {
  recorded:  { label: "Registrata", badge: "info", border: "border-l-suite-secondary", bg: "bg-suite-secondary-light/60" },
  approved:  { label: "Approvata",  badge: "ok",   border: "border-l-suite-accent",  bg: "bg-suite-accent-light/60"  },
  pending:   { label: "In attesa",  badge: "warn", border: "border-l-suite-warning", bg: "bg-suite-warning-light/60" },
  rejected:  { label: "Rifiutata",  badge: "err",  border: "border-l-suite-error",   bg: "bg-suite-error-light/40 opacity-70" },
  cancelled: { label: "Annullata",  badge: "neutral", border: "border-l-suite-neutral-400", bg: "bg-suite-neutral-100 opacity-60" },
};

/** Restituisce array di 42 date (6 settimane × 7 giorni) che include la
 *  griglia del mese, partendo da lunedì. */
function buildMonthGrid(year, month /* 1..12 */) {
  const first = new Date(year, month - 1, 1);
  // JS getDay(): Domenica=0..Sabato=6. Vogliamo Lunedì=0.
  const isoDay = (first.getDay() + 6) % 7;
  const start = new Date(year, month - 1, 1 - isoDay);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push(d);
  }
  return cells;
}

function toIso(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function inRange(iso, start, end) {
  return iso >= start && iso <= end;
}

/** Genera una tinta stabile (0-360) a partire dall'ID del dipendente,
 *  utile a distinguere visivamente i vari dipendenti nel mese. */
function hueFor(id) {
  if (!id) return 200;
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % 360;
  return h;
}

export default function LeaveCalendar() {
  const { user } = useAuth();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [kindFilter, setKindFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("recorded,approved,pending");
  const [employeeFilter, setEmployeeFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [companyId] = useCompanySelection();
  const [loading, setLoading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [detail, setDetail] = useState(null); // { date: iso, leaves: [...] }

  const grid = useMemo(() => buildMonthGrid(year, month), [year, month]);
  const fromIso = toIso(grid[0]);
  const toIsoEnd = toIso(grid[grid.length - 1]);

  const load = async () => {
    setLoading(true);
    try {
      if (!companyId) {
        setItems([]);
        setEmployees([]);
        setTotalEmployees(0);
        return;
      }
      const { data } = await api.get("/leave-calendar", {
        params: { statuses: statusFilter, from_date: fromIso, to_date: toIsoEnd, company_id: companyId },
      });
      const lr = Array.isArray(data?.items) ? data.items : [];
      const emps = Array.isArray(data?.employees) ? data.employees : [];
      setItems(lr);
      // v0.99.29 Fase 2: conta come "totale" solo i dipendenti attivi nel periodo
      // visualizzato (regola cessazione: cessation_date IS NULL OR >= start_iso).
      const inScopeEmps = Array.isArray(emps)
        ? emps.filter((e) => e.active !== false && (!e.cessation_date || e.cessation_date >= fromIso))
        : [];
      setEmployees(inScopeEmps);
      setTotalEmployees(inScopeEmps.length);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, statusFilter, companyId]);

  const filteredItems = useMemo(() => {
    return items.filter((item) => (
      (kindFilter === "all" || item.kind === kindFilter)
      && (employeeFilter === "all" || item.employee_id === employeeFilter)
    ));
  }, [items, kindFilter, employeeFilter]);

  /** Mappa iso → array di richieste che coprono quel giorno. */
  const dayMap = useMemo(() => {
    const m = new Map();
    for (const lr of filteredItems) {
      const iso = lr.date || lr.start_date;
      if (!iso || !inRange(iso, fromIso, toIsoEnd)) continue;
      if (!m.has(iso)) m.set(iso, []);
      m.get(iso).push(lr);
    }
    return m;
  }, [filteredItems, fromIso, toIsoEnd]);

  const goToday = () => {
    setYear(today.getFullYear());
    setMonth(today.getMonth() + 1);
  };

  /** Scarica il PDF del calendario stampabile (bacheca). */
  const downloadPdf = async () => {
    if (!companyId) {
      toast.error("Azienda non identificata — riprova dopo il caricamento della pagina");
      return;
    }
    setDownloadingPdf(true);
    try {
      const resp = await api.get("/reports/company/leave-calendar/pdf", {
        params: { company_id: companyId, year, month },
        responseType: "blob",
      });
      downloadBlob(resp.data, `calendario-assenze_${year}-${String(month).padStart(2, "0")}.pdf`, "application/pdf");
      toast.success("Calendario PDF scaricato");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setDownloadingPdf(false);
    }
  };

  const isCurrentMonth = (d) => d.getMonth() + 1 === month;
  const isToday = (d) => toIso(d) === toIso(today);
  const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;

  // Conteggi rapidi header
  const counts = useMemo(() => {
    const c = { total: filteredItems.length, employees: new Set(), byKind: {}, hours: 0 };
    for (const lr of filteredItems) {
      c.employees.add(lr.employee_id);
      c.byKind[lr.kind] = (c.byKind[lr.kind] || 0) + 1;
      c.hours += Number(lr.total_hours || lr.hours_per_day || 0);
    }
    return { ...c, employees: c.employees.size, hours: Math.round(c.hours * 100) / 100 };
  }, [filteredItems]);

  /** Statistiche per settimana ("Meteo settimanale") — 6 settimane della griglia mese.
   *  Per ogni settimana:
   *    - workingDays: numero di giorni lavorativi (Lun-Ven) che cadono NEL mese corrente
   *    - absencesTotal: somma delle assenze giorno×dipendente nei giorni lavorativi
   *    - peakDay: giorno con più assenze contemporanee
   *    - peakAbsences: numero max di dipendenti assenti in un singolo giorno
   *    - employeeNames: set unico di dipendenti coinvolti in assenze nella settimana
   *    - tone: "ok" (0 assenze picco), "warn" (1-2), "alert" (3+)
   */
  const weekStats = useMemo(() => {
    const stats = [];
    for (let w = 0; w < 6; w++) {
      const cells = grid.slice(w * 7, w * 7 + 7);
      // Se tutti i 7 giorni sono fuori dal mese corrente, salta la settimana
      const inMonth = cells.filter((d) => d.getMonth() + 1 === month);
      if (inMonth.length === 0) continue;
      let workingDays = 0;
      let absencesTotal = 0;
      let peakDay = null;
      let peakAbsences = 0;
      const names = new Set();
      for (const d of cells) {
        if (d.getMonth() + 1 !== month) continue;
        const dow = d.getDay(); // 0=dom 6=sab
        if (dow === 0 || dow === 6) continue; // solo giorni lavorativi
        workingDays++;
        const iso = toIso(d);
        const leaves = (dayMap.get(iso) || []).filter((l) => ["recorded", "approved", "pending"].includes(l.status));
        // Assenze uniche per dipendente in quel giorno
        const uniqEmps = new Set(leaves.map((l) => l.employee_id));
        absencesTotal += uniqEmps.size;
        for (const l of leaves) {
          if (l._employee_name) names.add(l._employee_name);
        }
        if (uniqEmps.size > peakAbsences) {
          peakAbsences = uniqEmps.size;
          peakDay = d;
        }
      }
      const tone = peakAbsences === 0 ? "ok" : peakAbsences <= 2 ? "warn" : "alert";
      stats.push({
        weekIndex: w + 1,
        firstDay: cells[0],
        lastDay: cells[6],
        workingDays,
        absencesTotal,
        peakDay,
        peakAbsences,
        employeeNames: Array.from(names).sort(),
        tone,
      });
    }
    return stats;
  }, [grid, dayMap, month]);

  return (
    <div className="space-y-2.5" data-testid="leave-calendar-page">
      {/* Header compatto (una riga, no subtitle) — A + E: nome mese cliccabile */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-baseline gap-3">
          <SuiteEyebrow icon={CalendarDays} className="!mb-0">Modulo Presenze</SuiteEyebrow>
          <h1 className="text-2xl font-bold tracking-tight text-suite-neutral-800" style={{ fontFamily: "Manrope" }}>
            Calendario Assenze
          </h1>
          <SuiteMonthPicker
            year={year}
            month={month}
            size="sm"
            align="start"
            testid="calendar-month-picker"
            onChange={({ year: nextYear, month: nextMonth }) => {
              setYear(nextYear);
              setMonth(nextMonth);
            }}
          />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <SuiteButton variant="ghost" size="sm" onClick={goToday} data-testid="calendar-today-btn">Oggi</SuiteButton>
          <div className="w-px h-6 bg-suite-neutral-200 mx-1" />
          <SuiteButton size="sm" icon={Printer} onClick={downloadPdf} loading={downloadingPdf}
                  disabled={!companyId || downloadingPdf} data-testid="calendar-download-pdf-btn">
            Stampa PDF
          </SuiteButton>
        </div>
      </div>

      {/* Info-strip fuse: contatore · filtri · micro-legenda — B + D */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-lg border border-suite-neutral-200 bg-suite-surface px-3 py-1.5 text-xs"
           data-testid="calendar-info-strip">
        <div className="flex items-center gap-1.5 text-suite-neutral-700">
          <Filter size={12} className="text-suite-primary" />
          <span className="font-semibold text-suite-neutral-800 tabular-nums">{counts.total}</span> assenze
          <span className="text-suite-neutral-300 mx-1">·</span>
          <span className="font-semibold text-suite-neutral-800 tabular-nums">{counts.employees}</span> dipendenti
          <span className="text-suite-neutral-300 mx-1">·</span>
          <span className="font-semibold text-suite-neutral-800 tabular-nums">
            {fmtHours(counts.hours)}
          </span> ore
        </div>
        <div className="w-px h-4 bg-suite-neutral-200" />
        <SuiteEmployeePicker employees={[...employees].sort((a, b) => `${a.last_name || ""} ${a.first_name || ""}`.localeCompare(
            `${b.last_name || ""} ${b.first_name || ""}`, "it", { sensitivity: "base" }
          ))} value={employeeFilter} onValueChange={setEmployeeFilter} includeAll allValue="all"
          className="w-[240px]" triggerClassName="h-8 text-xs" testId="calendar-employee-filter" placeholder="Dipendente" />
        <Select value={kindFilter} onValueChange={setKindFilter}>
          <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="calendar-kind-filter">
            <SelectValue placeholder="Causale" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le causali</SelectItem>
            {Object.entries(KIND_META).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="calendar-status-filter">
            <SelectValue placeholder="Stato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="recorded,approved,pending">Registrate + pianificate</SelectItem>
            <SelectItem value="recorded">Solo registrate nei cartellini</SelectItem>
            <SelectItem value="approved,pending">Solo richieste pianificate</SelectItem>
            <SelectItem value="approved">Solo approvate</SelectItem>
            <SelectItem value="pending">Solo in attesa</SelectItem>
            <SelectItem value="all">Tutte</SelectItem>
          </SelectContent>
        </Select>
        <div className="w-px h-4 bg-suite-neutral-200" />
        {/* Micro-indicatore compatto settimane serene (sostituisce la vecchia sidebar "Meteo") */}
        {weekStats && weekStats.length > 0 && (() => {
          const serene = weekStats.filter((w) => w.tone === "ok").length;
          const totWeeks = weekStats.length;
          return (
            <div className="flex items-center gap-1.5 text-[11px] text-suite-neutral-500" title="Settimane senza assenze nel mese"
                 data-testid="weeks-summary">
              <span className="text-suite-primary/70">☀</span>
              <span><span className="font-semibold text-suite-neutral-800 tabular-nums">{serene}</span>/{totWeeks} sett. serene</span>
            </div>
          );
        })()}
        <div className="w-px h-4 bg-suite-neutral-200" />
        {/* Micro-legenda compatta, integrata */}
        <div className="flex items-center gap-2 text-suite-neutral-500 text-[11px]" title="Legenda stati richieste">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-suite-secondary" /> Registrata</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-suite-accent" /> Approvata</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-suite-warning" /> Attesa</span>
          <span className="inline-flex items-center gap-1" title="Richiesta rifiutata dall'admin"><span className="w-2 h-2 rounded-sm bg-suite-error opacity-70" /> Rifiutata</span>
          <span className="inline-flex items-center gap-1" title="Richiesta annullata dal dipendente"><span className="w-2 h-2 rounded-sm bg-suite-neutral-400" /> Annullata</span>
        </div>
      </div>

      {/* Griglia calendario a full-width (Calendario Assenze = pagina di consultazione) */}
      <SuiteCard padding="none" testid="calendar-grid-card">
        <div className="grid grid-cols-7 border-b border-suite-neutral-200 bg-suite-neutral-50">
          {WEEKDAYS_SHORT.map((w, i) => (
            <div key={w}
                 className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] font-semibold ${i >= 5 ? "text-suite-neutral-400" : "text-suite-neutral-600"}`}>
              {w}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 auto-rows-[minmax(96px,1fr)]" data-testid="calendar-grid">
          {grid.map((d, i) => {
            const iso = toIso(d);
            const dayLeaves = dayMap.get(iso) || [];
            const currentMonth = isCurrentMonth(d);
            const weekend = isWeekend(d);
            const isTd = isToday(d);
            // Limitiamo a 3 pill visibili + "+N altri"
            const visible = dayLeaves.slice(0, 3);
            const extra = dayLeaves.length - visible.length;
            return (
              <div
                key={i}
                onClick={() => dayLeaves.length && setDetail({ date: iso, leaves: dayLeaves })}
                data-testid={`calendar-day-${iso}`}
                className={`
                  relative border-r border-b border-suite-neutral-200 p-1.5 flex flex-col gap-1 min-w-0
                  ${currentMonth ? "bg-suite-surface" : "bg-suite-neutral-50/50"}
                  ${weekend && currentMonth ? "bg-suite-neutral-50/70" : ""}
                  ${dayLeaves.length ? "cursor-pointer hover:bg-suite-primary-light/30" : ""}
                  transition-colors
                `}
              >
                <div className="flex items-center justify-between">
                  <span className={`
                    text-xs font-semibold w-6 h-6 flex items-center justify-center rounded-full
                    ${isTd ? "bg-suite-primary text-white" : ""}
                    ${!isTd && currentMonth ? "text-suite-neutral-800" : ""}
                    ${!isTd && !currentMonth ? "text-suite-neutral-400" : ""}
                  `}>
                    {d.getDate()}
                  </span>
                  {dayLeaves.length > 0 && (
                    <span className="text-[10px] font-bold text-suite-primary tabular-nums">
                      {dayLeaves.length}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-0.5 min-w-0 overflow-hidden">
                  {visible.map((lr) => {
                    const meta = STATUS_META[lr.status] || STATUS_META.approved;
                    const hue = hueFor(lr.employee_id);
                    const isConfirmed = lr.status === "approved" || lr.status === "recorded";
                    return (
                      <div key={lr.id}
                           title={`${lr._employee_name} — ${KIND_META[lr.kind]?.label || lr.kind} (${meta.label})`}
                           className={`
                             text-[10px] px-1.5 py-0.5 rounded-sm border-l-2 truncate flex items-center gap-1
                             ${meta.border} ${meta.bg}
                             ${lr.status === "cancelled" ? "border-dashed" : ""}
                           `}
                           style={isConfirmed ? { backgroundColor: `hsl(${hue} 70% 92%)`, color: `hsl(${hue} 45% 25%)` } : undefined}
                           data-testid={`calendar-pill-${lr.id}`}
                      >
                        <span className="font-semibold truncate flex-1">
                          {lr._employee_last_name || lr._employee_name?.split(" ")[0] || "—"}
                        </span>
                        <span className="opacity-70 flex-shrink-0">
                          {(KIND_META[lr.kind]?.label || lr.kind).slice(0, 4)}.
                        </span>
                      </div>
                    );
                  })}
                  {extra > 0 && (
                    <div className="text-[10px] text-suite-primary font-semibold pl-1">
                      + {extra} altri
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </SuiteCard>

      {/* Loading overlay */}
      {loading && (
        <div className="text-center text-xs text-suite-neutral-500 py-2">
          Caricamento assenze...
        </div>
      )}

      {/* Dialog dettaglio giorno */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg">
          {detail && (() => {
            const d = new Date(detail.date + "T00:00:00");
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
                    <CalendarDays size={18} className="text-suite-primary" />
                    {formatDateLongIT(d)}
                  </DialogTitle>
                  <DialogDescription>
                    {detail.leaves.length} dipendente/i in assenza in questa data
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-2 mt-2 max-h-[440px] overflow-y-auto pr-1">
                  {detail.leaves.map((lr) => {
                    const meta = STATUS_META[lr.status] || STATUS_META.approved;
                    const kindMeta = KIND_META[lr.kind] || {};
                    const Icon = kindMeta.icon || Users2;
                    return (
                      <div key={lr.id}
                           className={`border border-suite-neutral-200 rounded-md p-3 flex items-start gap-3 ${meta.bg}`}
                           data-testid={`calendar-detail-item-${lr.id}`}>
                        <div className="w-9 h-9 rounded-md bg-suite-surface border border-suite-neutral-200 flex items-center justify-center flex-shrink-0">
                          <Icon size={16} className="text-suite-primary" strokeWidth={1.75} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-semibold text-suite-neutral-800 truncate">
                              {lr._employee_name || "—"}
                            </div>
                            <SuiteBadge tone={meta.badge} size="sm">{meta.label}</SuiteBadge>
                          </div>
                          <div className="text-xs text-suite-neutral-600 mt-0.5">
                            {kindMeta.label || lr.kind} · {lr.date || lr.start_date}
                          </div>
                          <div className="text-xs text-suite-neutral-500 mt-0.5">
                            {fmtHours(lr.total_hours || 0)} h
                            {" · "}
                            {lr.source === "attendance" ? "Registrata nel cartellino" : "Richiesta dal portale"}
                            {lr.notes ? <> · <span className="italic">{lr.notes}</span></> : null}
                          </div>
                          {lr.rejection_reason && (
                            <div className="text-xs text-suite-error mt-1">Motivo rifiuto: {lr.rejection_reason}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

